// Vista del jugador en su team: toggle sync, preview del payload, salir.

import React, { useState } from 'react';
import { useT } from '../../lib/i18n';
import { TeamContext } from '../../hooks/useTeam';
import { supabase, SUPABASE_ENABLED } from '../../lib/supabase';
import { payloadToDerived } from './payloadToStats';

// ── Resumen de stats sincronizados ───────────────────────────────────────────

function StatPill({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', minWidth: '72px' }}>
      <span style={{ fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)' }}>{value}</span>
    </div>
  );
}

function SyncedStatsSummary({ row }: { row: { stats_payload: any; hands_count: number; updated_at: string; sync_count: number } }) {
  const d = payloadToDerived(row.stats_payload);
  const meta = [
    `${(row.hands_count ?? 0).toLocaleString()} manos`,
    `sync #${row.sync_count ?? 1}`,
    row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '',
  ].filter(Boolean).join(' · ');

  const gridStyle: React.CSSProperties = {
    display: 'flex', flexWrap: 'wrap', gap: '1rem',
    padding: '0.75rem',
    background: '#0d1117',
    border: '1px solid #1e2636',
    borderRadius: '4px',
    marginBottom: '0.5rem',
  };

  if (!d) {
    return (
      <p style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: 0 }}>
        Sin datos de stats en este sync.
      </p>
    );
  }

  return (
    <div>
      <p style={{ fontSize: '0.72rem', color: 'var(--muted)', margin: '0 0 0.5rem' }}>{meta}</p>
      <div style={{ fontSize: '0.62rem', color: 'var(--muted)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Preflop</div>
      <div style={gridStyle}>
        <StatPill label="VPIP"      value={`${d.vpip}%`} />
        <StatPill label="PFR"       value={`${d.pfr}%`} />
        <StatPill label="3Bet"      value={`${d.threebet}%`} />
        <StatPill label="Fold/3Bet" value={`${d.foldTo3bet}%`} />
        <StatPill label="ATS"       value={`${d.ats}%`} />
        {(d.openLimp ?? 0) > 0 && <StatPill label="Open Limp" value={`${d.openLimp}%`} />}
      </div>
      <div style={{ fontSize: '0.62rem', color: 'var(--muted)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Postflop</div>
      <div style={gridStyle}>
        <StatPill label="CBet Flop" value={`${d.cbetFlop}%`} />
        <StatPill label="CBet Turn" value={`${d.cbetTurn}%`} />
        <StatPill label="Fold/CBet" value={`${d.foldToCbetFlop}%`} />
        <StatPill label="WTSD"      value={`${d.wtsd}%`} />
        <StatPill label="WWSF"      value={`${d.wwsf}%`} />
        <StatPill label="W@SD"      value={`${d.wsd}%`} />
        <StatPill label="AF"        value={`${d.af}`} />
      </div>
    </div>
  );
}

interface Props {
  ctx:          TeamContext;
  onToggleSync: (enabled: boolean) => Promise<boolean>;
  onLeave:      (anonymize: boolean) => Promise<boolean>;
}

export function PlayerTeamView({ ctx, onToggleSync, onLeave }: Props) {
  const { t } = useT();
  const { team, membership } = ctx;

  const [syncLoading,   setSyncLoading]   = useState(false);
  const [showPayload,   setShowPayload]   = useState(false);
  const [payload,       setPayload]       = useState<object | null>(null);
  const [payloadLoading,setPayloadLoading]= useState(false);
  const [showLeaveModal,setShowLeaveModal]= useState(false);
  const [leaveAnonymize,setLeaveAnonymize]= useState(true);
  const [leaving,       setLeaving]       = useState(false);
  const [leaveError,    setLeaveError]    = useState<string | null>(null);
  const [syncError,     setSyncError]     = useState<string | null>(null);

  // ── Toggle sync ──────────────────────────────────────────────────────────────

  const handleToggleSync = async () => {
    setSyncLoading(true);
    setSyncError(null);
    const ok = await onToggleSync(!membership.syncEnabled);
    if (!ok) setSyncError(t('teams.syncToggleError'));
    setSyncLoading(false);
  };

  // ── Preview del último payload enviado ───────────────────────────────────────

  const handleShowPayload = async () => {
    if (showPayload) { setShowPayload(false); return; }

    setPayloadLoading(true);
    if (!SUPABASE_ENABLED) { setPayloadLoading(false); return; }

    const { data } = await supabase
      .from('team_stats')
      .select('stats_payload, updated_at, hands_count, sync_count')
      .eq('team_id', team.id)
      .eq('user_id', membership.userId)
      .maybeSingle();

    setPayload(data ?? null);
    setShowPayload(true);
    setPayloadLoading(false);
  };

  // ── Leave team ───────────────────────────────────────────────────────────────

  const handleLeaveConfirm = async () => {
    setLeaving(true);
    setLeaveError(null);
    const ok = await onLeave(leaveAnonymize);
    if (!ok) {
      setLeaveError(t('teams.leaveError'));
      setLeaving(false);
    }
    // Si ok === true, el hook ya removió el team del estado → el componente se desmonta
  };

  // ── Estilos ──────────────────────────────────────────────────────────────────

  const card: React.CSSProperties = {
    background: '#111520',
    border: '1px solid #1e2636',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1rem',
    fontFamily: 'var(--mono)',
  };

  const label: React.CSSProperties = {
    fontSize: '0.7rem',
    color: 'var(--muted)',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    marginBottom: '0.25rem',
  };

  const value: React.CSSProperties = {
    fontSize: '0.9rem',
    color: 'var(--text)',
  };

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto' }}>

      {/* Header del team */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <span style={{ background: '#4a9ae022', border: '1px solid #4a9ae055', color: '#4a9ae0', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '2px', padding: '3px 10px', borderRadius: '3px' }}>
            TEAM
          </span>
          <h2 style={{ margin: 0, fontSize: '1rem', color: 'var(--text)', letterSpacing: '1px' }}>{team.name}</h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <div style={label}>{t('teams.statusActive')}</div>
            <div style={value}>
              {membership.status === 'active'
                ? <span style={{ color: '#52c2a0' }}>● {t('teams.statusActive')}</span>
                : <span style={{ color: '#e0a852' }}>● {t('teams.statusInvited')}</span>
              }
            </div>
          </div>
          {membership.joinedAt && (
            <div>
              <div style={label}>{t('teams.memberSince')}</div>
              <div style={value}>{new Date(membership.joinedAt).toLocaleDateString()}</div>
            </div>
          )}
        </div>
      </div>

      {/* Toggle sync */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
          <div>
            <div style={{ ...label, marginBottom: '0.4rem' }}>{t('teams.syncTitle')}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)', lineHeight: 1.5, maxWidth: '420px' }}>
              {t('teams.syncDescription')}
            </div>
          </div>
          <button
            onClick={handleToggleSync}
            disabled={syncLoading}
            style={{
              background:    membership.syncEnabled ? '#52c2a0' : '#1e2636',
              color:         membership.syncEnabled ? '#111520'  : 'var(--muted)',
              border:        'none',
              borderRadius:  '4px',
              padding:       '0.5rem 1rem',
              fontSize:      '0.75rem',
              fontWeight:    700,
              letterSpacing: '1px',
              cursor:        syncLoading ? 'not-allowed' : 'pointer',
              fontFamily:    'var(--display)',
              flexShrink:    0,
              marginLeft:    '1rem',
            }}
          >
            {syncLoading
              ? '...'
              : membership.syncEnabled ? t('teams.syncOn') : t('teams.syncOff')
            }
          </button>
        </div>
        {syncError && (
          <p style={{ color: '#e05252', fontSize: '0.78rem', margin: '0.5rem 0 0' }}>{syncError}</p>
        )}
      </div>

      {/* Preview del payload */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div style={label}>{t('teams.payloadTitle')}</div>
          <button
            className="btn btn-secondary"
            onClick={handleShowPayload}
            disabled={payloadLoading}
            style={{ fontSize: '0.72rem', padding: '0.3rem 0.75rem' }}
          >
            {payloadLoading ? '...' : showPayload ? t('teams.payloadHideBtn') : t('teams.payloadShowBtn')}
          </button>
        </div>

        <p style={{ fontSize: '0.78rem', color: 'var(--muted)', margin: '0 0 0.75rem' }}>
          {t('teams.payloadDescription')}
        </p>

        {showPayload && (
          payload
            ? <SyncedStatsSummary row={payload as any} />
            : (
              <p style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: 0 }}>
                {t('teams.payloadEmpty')}
              </p>
            )
        )}
      </div>

      {/* Salir del team */}
      <div style={{ ...card, borderColor: '#2a1a1a' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{team.name}</span>
          <button
            className="btn btn-danger"
            onClick={() => setShowLeaveModal(true)}
            style={{ fontSize: '0.75rem' }}
          >
            {t('teams.leaveBtn')}
          </button>
        </div>
      </div>

      {/* Modal de confirmación de salida */}
      {showLeaveModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ ...card, maxWidth: '480px', width: '100%', marginBottom: 0 }}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', color: 'var(--text)' }}>
              {t('teams.leaveModalTitle')}
            </h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginBottom: '1.25rem', lineHeight: 1.5 }}>
              {t('teams.leaveModalBody')}
            </p>

            {/* Opciones de datos históricos */}
            {[
              { val: true,  label: t('teams.leaveOptionAnon') },
              { val: false, label: t('teams.leaveOptionDelete') },
            ].map(opt => (
              <label key={String(opt.val)} style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.75rem', cursor: 'pointer', alignItems: 'flex-start' }}>
                <input
                  type="radio"
                  checked={leaveAnonymize === opt.val}
                  onChange={() => setLeaveAnonymize(opt.val)}
                  style={{ marginTop: '3px', flexShrink: 0 }}
                />
                <span style={{ fontSize: '0.8rem', color: 'var(--text)', lineHeight: 1.5 }}>{opt.label}</span>
              </label>
            ))}

            {leaveError && (
              <p style={{ color: '#e05252', fontSize: '0.78rem', margin: '0 0 0.75rem' }}>{leaveError}</p>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button
                className="btn btn-danger"
                onClick={handleLeaveConfirm}
                disabled={leaving}
                style={{ flex: 1 }}
              >
                {leaving ? t('teams.leaving') : t('teams.leaveConfirmBtn')}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowLeaveModal(false)}
                disabled={leaving}
              >
                {t('teams.leaveCancelBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
